/**
 * Create a community listing (title, category, description, price, condition, location, photos).
 */
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { communityListingsService } from '../services/communityListingsService';
import { AUTO_REMOVE_DAYS, MAX_PHOTOS } from '../constants/communityListings';
import { YButton } from '../../../ui/YButton';
import { CommunityListingPageShell } from './CommunityListingPageShell';
import { CommunityListingFormFields } from './CommunityListingFormFields';
import './community-listing-pages.css';

export function CommunityListingCreate() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState('');
  const [error, setError] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [form, setForm] = useState({
    title: '',
    category: '',
    description: '',
    price: '',
    condition: '',
    location_text: '',
    latitude: null,
    longitude: null,
  });

  const handleFieldChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handlePhotoSelect = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const images = files.filter((f) => f.type.startsWith('image/'));
    setPhotoFiles((prev) => [...prev, ...images].slice(0, MAX_PHOTOS));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!form.category) {
      setError('Category is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSubmitPhase('creating');
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        description: (form.description || '').trim(),
        condition: (form.condition || '').trim() || undefined,
        location_text: (form.location_text || '').trim() || undefined,
      };
      if (form.latitude != null && form.longitude != null) {
        payload.latitude = form.latitude;
        payload.longitude = form.longitude;
      }
      if (form.price !== '' && form.price != null) {
        const p = parseFloat(form.price);
        if (!Number.isNaN(p) && p >= 0) payload.price = String(p);
      }
      const created = await communityListingsService.create(payload);
      const toUpload = (photoFiles || []).slice(0, MAX_PHOTOS);
      if (toUpload.length > 0) {
        setSubmitPhase('uploading');
        for (let i = 0; i < toUpload.length; i += 1) {
          await communityListingsService.uploadListingPhoto(created.id, toUpload[i], i + 1);
        }
      }
      navigate(`/camp-essentials/gear/listings/${created.id}`, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.response?.data?.title?.[0] ||
        err?.message ||
        'Could not create listing. Try again.';
      setError(typeof msg === 'string' ? msg : 'Could not create listing.');
    } finally {
      setSubmitting(false);
      setSubmitPhase('');
    }
  };

  return (
    <CommunityListingPageShell
      title="Post a listing"
      subtitle="Marketplace"
      testId="community-listing-create"
    >
      <p className="community-listing-form__banner">
        Listings are automatically removed after {AUTO_REMOVE_DAYS} days. Add up to {MAX_PHOTOS}{' '}
        photos (optional).
      </p>

      <form onSubmit={handleSubmit}>
        <CommunityListingFormFields
          form={form}
          onFieldChange={handleFieldChange}
          onLocationTextChange={(v) =>
            setForm((prev) => ({ ...prev, location_text: (v || '').slice(0, 120) }))
          }
          onLocationSelect={(place) =>
            setForm((prev) => ({
              ...prev,
              location_text: (place.label || place.name || '').slice(0, 120),
              latitude: place.latitude != null ? place.latitude : null,
              longitude: place.longitude != null ? place.longitude : null,
            }))
          }
          showPhotos
          photoFiles={photoFiles}
          fileInputRef={fileInputRef}
          onPhotoSelect={handlePhotoSelect}
          onClearPhotos={() => setPhotoFiles([])}
        />

        {error ? <div className="community-listing-form__error">{error}</div> : null}

        <div className="community-listing-form__actions">
          <YButton type="submit" disabled={submitting}>
            {submitting
              ? submitPhase === 'uploading'
                ? 'Uploading photos…'
                : 'Posting…'
              : 'Post listing'}
          </YButton>
          <YButton
            type="button"
            variant="outline-secondary"
            onClick={() => navigate('/camp-essentials/gear?tab=listings')}
          >
            Cancel
          </YButton>
        </div>
      </form>
    </CommunityListingPageShell>
  );
}
